import type { Location } from 'history'
import omit from 'lodash/omit'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useUserOrganizationMembership } from '@modules-deprecated/app/organization/hooks/useUserOrganizationMembership'

import { getSimplifiedRouteNameForSegmentTracking, sendTrackEventBeacon, useSegment } from '../../../hooks'
import { useOrganizationFiscalYears } from '../../../hooks/useOrganizationFiscalYears'
import { HASH_ROUTER_LOAD_SAFETY_TIMEOUT } from '../../../hooks/useSegmentPageEvent'
import { getRouteParams } from '../../../utils/routeParams'
import type { ReportSession } from '../types/expertReportTracking'
import { closeReportSession } from '../utils/closeReportSession'
import { getDefaultReportPeriod } from '../utils/getDefaultReportPeriod'
import { getReportSessionTransition } from '../utils/getReportSessionTransition'
import { useExpertReportsAvailability } from './useExpertReportsAvailability'

const MEMBERSHIP_LOAD_TIMEOUT = 2000

const PAGE_UNLOAD_EVENTS = ['beforeunload', 'pagehide'] as const

const getRouteName = (location: Location) =>
  getSimplifiedRouteNameForSegmentTracking(location, omit(getRouteParams(), '*'))

const togglePageUnloadListener = (listener: () => void, isEnabled: boolean) => {
  for (const eventName of PAGE_UNLOAD_EVENTS) {
    if (isEnabled) {
      window.addEventListener(eventName, listener)
    } else {
      window.removeEventListener(eventName, listener)
    }
  }
}

export const useTrackExpertReportEntry = () => {
  const location = useLocation()
  const { getDefaultPayload, track } = useSegment()
  const { organization } = useUserOrganization()
  const { membership } = useUserOrganizationMembership()
  const [membershipTimedOutOrganizationId, setMembershipTimedOutOrganizationId] = useState<string>()
  const availability = useExpertReportsAvailability()
  const { defaultYear } = useOrganizationFiscalYears()
  const defaultEncodedPeriod = getDefaultReportPeriod({ fiscalYear: defaultYear, organizationId: organization?.id })

  const sessionRef = useRef<ReportSession | undefined>(undefined)
  const previousRouteNameRef = useRef<string | undefined>(undefined)
  const settledRouteNameRef = useRef<string | undefined>(undefined)
  // Both senders change identity whenever the organization refetches; holding them in refs keeps the dwell callback
  // stable so the unmount cleanup cannot fire a Time Spent event on an ordinary re-render.
  const trackRef = useRef(track)
  const getDefaultPayloadRef = useRef(getDefaultPayload)

  useEffect(() => {
    trackRef.current = track
    getDefaultPayloadRef.current = getDefaultPayload
  })

  const trackTimeSpent = useCallback((isPageUnloading = false) => {
    const { session, timeSpentEvent } = closeReportSession(sessionRef.current, Date.now())

    sessionRef.current = session

    if (!timeSpentEvent) {
      return
    }

    const { name, organization: sessionOrganization, payload } = timeSpentEvent

    if (isPageUnloading) {
      sendTrackEventBeacon(name, { ...getDefaultPayloadRef.current({ organization: sessionOrganization }), ...payload })

      return
    }

    trackRef.current(name, payload, { organization: sessionOrganization })
  }, [])

  // Chrome can destroy the outgoing renderer before it hands over a beacon queued at `pagehide`, so the dwell event
  // is flushed at `beforeunload`, which runs earlier while the page is still alive and, unlike `visibilitychange`,
  // does not fire on a tab switch. `pagehide` stays as a backstop, guarded by `isClosed`. The session is not reopened
  // on a back-forward-cache restore: that would emit a second Time Spent without a matching Viewed.
  const handlePageUnload = useCallback(() => {
    trackTimeSpent(true)
  }, [trackTimeSpent])

  useEffect(
    () => () => {
      togglePageUnloadListener(handlePageUnload, false)
      trackTimeSpent()
    },
    [handlePageUnload, trackTimeSpent],
  )

  useEffect(() => {
    if (!organization?.id || membership) {
      return
    }

    const organizationId = organization.id
    const timeoutId = setTimeout(() => {
      setMembershipTimedOutOrganizationId(organizationId)
    }, MEMBERSHIP_LOAD_TIMEOUT)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [membership, organization?.id])

  const isMembershipSettled = !!membership || membershipTimedOutOrganizationId === organization?.id

  useEffect(() => {
    // `useSegment` attaches organization_id from this and actor_type from the membership. On a hard load either can be
    // missing on the first render. Without an organization the event is unattributable; a membership that never
    // arrives is treated as a non-CPA, same as `useSegment`.
    if (!organization || !isMembershipSettled) {
      return
    }

    const { events, session } = getReportSessionTransition({
      availability,
      defaultEncodedPeriod,
      location,
      now: Date.now(),
      organization,
      previousRouteName: previousRouteNameRef.current,
      previousSession: sessionRef.current,
    })

    sessionRef.current = session
    togglePageUnloadListener(handlePageUnload, !!session && !session.isClosed)

    for (const { name, organization: eventOrganization, payload } of events) {
      trackRef.current(name, payload, { organization: eventOrganization })
    }
  }, [availability, defaultEncodedPeriod, handlePageUnload, isMembershipSettled, location, organization])

  useEffect(() => {
    // Route params are stored by other React roots and by Ember only after this effect has run, so reading them right
    // away can leave the previous route's ids unredacted. A route left before they settle falls back to current params.
    const timeoutId = setTimeout(() => {
      settledRouteNameRef.current = getRouteName(location)
    }, HASH_ROUTER_LOAD_SAFETY_TIMEOUT)

    return () => {
      clearTimeout(timeoutId)
      previousRouteNameRef.current = settledRouteNameRef.current ?? getRouteName(location)
      settledRouteNameRef.current = undefined
    }
  }, [location])
}
